import { useMemo } from 'react';
import { useDraftParams } from '@/hooks/use-draft-params';

import { AiContext } from '@/components/ai-context';

import { useDocumentId } from '@/hooks/use-document-id';
import { useHeldValue } from '@/hooks/use-held-value';
import { useNote, useBlocks, useNoteWriter, useResolvedInstructionIds } from '@/hooks/rxdb-models';

import { appRoutes } from '@/constants/routes';
import { type GroupedInstruction } from '@/components/instructions-panel';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';

export const NoteAiContext = () => {
  const { id, isDraft } = useDocumentId();
  const { data: note, isLoading: isLoadingNote } = useNote(!isDraft ? id : undefined);

  const { draftType: newNoteVariant } = useDraftParams();

  const { mutate: upsertNote } = useNoteWriter(note, id, { variant: newNoteVariant, createIfMissing: isDraft });

  const navigateWithSearch = useNavigateWithSearch();

  const noteJson = useMemo(() => note?.toJSON(), [note]);

  const { data: blocks, isLoading: isLoadingBlocks } = useBlocks(note?.blockIds);

  const instructionIds = useMemo(() => {
    const blockInstructionIds = Object.values(noteJson?.blockInstructions ?? {}).flatMap(Object.keys);
    return Array.from(new Set([...Object.keys(noteJson?.instructions ?? {}), ...blockInstructionIds]));
  }, [noteJson]);

  const resolvedInstructionIds = useResolvedInstructionIds(instructionIds);

  const instructionCount = useMemo(() => {
    const enabledMaps = [noteJson?.instructions ?? {}, ...Object.values(noteJson?.blockInstructions ?? {})];
    return {
      total: resolvedInstructionIds.length,
      enabled: resolvedInstructionIds.filter((id) => enabledMaps.some((map) => map[id])).length,
    };
  }, [resolvedInstructionIds, noteJson]);

  const groupedInstructions = useMemo((): GroupedInstruction[] => {
    if (!note) return [];

    const hasBlockInstructionGroups = !!blocks?.length && Object.keys(note.blockInstructions || {}).length > 0;
    if (!hasBlockInstructionGroups) return [];

    const noteGroup: GroupedInstruction = {
      id: note.id,
      name: note.name ?? '',
      type: note.variant,
      variant: note.variant,
      instructions: noteJson?.instructions ?? {},
    };

    const blockGroups = (blocks ?? []).map((block) => ({
      ...block.toJSON(),
      type: 'blocks',
      instructions: note.blockInstructions?.[block.id] ?? {},
    }));

    return [noteGroup, ...blockGroups];
  }, [note, noteJson, blocks]);

  // While a note switch swaps the blocks query, `note` is already the new note but `blocks`
  // still holds the previous note's docs, so recomputing here yields a cross-note count. The
  // loading flags stay true through every mid-swap window (and through first mount), so gating
  // on them alone keeps the derived values from flashing an intermediate result; hold the last
  // settled values otherwise.
  const isSettled = !isLoadingNote && !isLoadingBlocks;

  const {
    instructionCount: displayedInstructionCount,
    groupedInstructions: displayedGroupedInstructions,
    documentInstructions: displayedDocumentInstructions,
  } = useHeldValue({ instructionCount, groupedInstructions, documentInstructions: note?.instructions }, isSettled);

  const onInstructionCreate = async (instructionId: string) => {
    const savedNote = await upsertNote({
      instructions: {
        ...note?.instructions,
        [instructionId]: true,
      },
      variant: !note ? newNoteVariant : note.variant,
    });
    navigateWithSearch(`${appRoutes.library}/${savedNote.id}`, {
      replace: true,
    });
  };

  const onInstructionEnabledChange = (instructionId: string, enabled: boolean, blockId?: string) => {
    if (blockId) {
      return upsertNote({
        blockInstructions: {
          ...note?.blockInstructions,
          [blockId]: {
            ...note?.blockInstructions?.[blockId],
            [instructionId]: enabled,
          },
        },
      });
    }

    return upsertNote({
      instructions: {
        ...note?.instructions,
        [instructionId]: enabled,
      },
    });
  };

  return (
    <AiContext
      key={id}
      instructionCount={displayedInstructionCount}
      documentInstructions={displayedDocumentInstructions}
      groupedInstructions={displayedGroupedInstructions}
      instructionsLoading={!isSettled}
      // During the hold window the visible rows belong to the previous note while the write
      // callbacks close over the live (new/undefined) note, so gate them until settled to avoid
      // building a payload from the wrong or undefined note (e.g. ...note?.instructions).
      onInstructionCreate={isSettled ? onInstructionCreate : undefined}
      onInstructionEnabledChange={isSettled ? onInstructionEnabledChange : noop}
    />
  );
};

const noop = () => {};
